import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { ApiError, apiFetch } from './client';
import type { AuthenticatedUser, LoginResult } from './types';

const TOKEN_STORAGE_KEY = 'rfp.token';

function readStoredToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredToken(token: string | null): void {
  try {
    if (token === null) {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    } else {
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
    }
  } catch {
    // Storage unavailable (private mode etc.) — session continues in memory.
  }
}

export interface AuthContextValue {
  user: AuthenticatedUser | null;
  token: string | null;
  /** True while a stored token is being validated against GET /auth/me. */
  isHydrating: boolean;
  login: (email: string, password: string) => Promise<AuthenticatedUser>;
  /** Installs a session obtained outside login (e.g. invitation signup). */
  adoptSession: (result: LoginResult) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(readStoredToken);
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isHydrating, setIsHydrating] = useState<boolean>(() => readStoredToken() !== null);

  useEffect(() => {
    const storedToken = readStoredToken();

    if (storedToken === null) {
      return;
    }

    const controller = new AbortController();

    apiFetch<AuthenticatedUser>('/auth/me', { token: storedToken, signal: controller.signal })
      .then((me) => {
        setUser(me);
        setIsHydrating(false);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) {
          return;
        }

        if (error instanceof ApiError && error.status === 401) {
          writeStoredToken(null);
          setToken(null);
        }

        setIsHydrating(false);
      });

    return () => controller.abort();
  }, []);

  const adoptSession = useCallback((result: LoginResult) => {
    writeStoredToken(result.token);
    setToken(result.token);
    setUser(result.user);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await apiFetch<LoginResult>('/auth/login', {
        method: 'POST',
        body: { email, password },
      });

      adoptSession(result);

      return result.user;
    },
    [adoptSession],
  );

  const logout = useCallback(() => {
    const currentToken = readStoredToken();

    if (currentToken !== null) {
      // Best-effort server-side session invalidation.
      apiFetch('/auth/logout', { method: 'POST', token: currentToken }).catch(() => undefined);
    }

    writeStoredToken(null);
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, token, isHydrating, login, adoptSession, logout }),
    [user, token, isHydrating, login, adoptSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }

  return context;
}

/** Redirects unauthenticated visitors to /login, preserving their target. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, isHydrating } = useAuth();
  const location = useLocation();

  if (isHydrating) {
    return null;
  }

  if (user === null) {
    return <Navigate replace state={{ from: location.pathname }} to="/login" />;
  }

  return <>{children}</>;
}

/** Restricts a route to users holding the `admin` role. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, isHydrating } = useAuth();

  if (isHydrating) {
    return null;
  }

  if (user === null || !user.roles.includes('admin')) {
    return <Navigate replace to="/dashboard" />;
  }

  return <>{children}</>;
}
